import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from 'pdfmake/build/vfs_fonts';
import useDate from "../day";
import figureFormatter from "@/services/utils/figureFormatter";
import { loadImageAsBase64 } from "@/services/utils/global";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

pdfMake.fonts = {
  SulaimanLipi: {
    normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
    bold: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
    italics: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
    bolditalics: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff'
  }
};

const printMushak43 = () => {
  const { formatDate } = useDate();
  const { commaFormat } = figureFormatter();

  const exportToPdfMushak43 = async (companyInfo, productCosting) => {
    const imagePath = await loadImageAsBase64('/app-assets/images/gov_seal.png');

    const doc = {
      pageSize: "A4",
      pageOrientation: "landscape",
      pageMargins: [25, 25, 25, 40],
      defaultStyle: {
        font: "SulaimanLipi",
        fontSize: 8
      },
      content: getContent(companyInfo, productCosting, imagePath),
      footer: getFooter(),
      info: {
        title: 'মূসক-৪.৩'
      },
      styles: styles
    };

    pdfMake.createPdf(doc).open();
  }

  const getContent = (companyInfo, productCosting, imagePath) => {
    return [
      getHeaderBlock(imagePath),
      { text: 'উপকরণ-উৎপাদ সহগ (Input-Output Coefficient) ঘোষণা', style: 'h2', margin: [0, 6, 0, 0] },
      { text: '(বিধি ২১ দ্রষ্টব্য)', style: 'small', margin: [0, 0, 0, 10] },
      getCompanyBlock(companyInfo, productCosting),
      getItemTable(productCosting),
      getNoteBlock()
    ];
  }

  const getHeaderBlock = (imagePath) => {
    const seal = getSeal(imagePath);
    return {
      columns: [
        seal,
        {
          width: '*',
          stack: [
            { text: 'গণপ্রজাতন্ত্রী বাংলাদেশ সরকার', style: 'h1' },
            { text: 'জাতীয় রাজস্ব বোর্ড, ঢাকা', style: 'h1', margin: [0, 3, 0, 0] }
          ]
        },
        {
          width: 55,
          margin: [0, 12, 0, 0],
          table: { widths: ['*'], body: [[{ text: 'মূসক - ৪.৩', alignment: 'center', fontSize: 8 }]] },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => '#000',
            vLineColor: () => '#000'
          }
        }
      ]
    };
  }

  const getSeal = (imagePath) => {
    if (! imagePath) return { width: 45, text: '' };
    return { image: imagePath, width: 45, height: 45 };
  }

  const getCompanyBlock = (companyInfo, productCosting) => {
    return {
      layout: 'noBorders',
      margin: [0, 0, 0, 10],
      table: {
        widths: [250, '*'],
        body: [
          [
            { text: '১। প্রতিষ্ঠানের নামঃ', style: 'info' },
            { text: getValue(companyInfo.name), style: 'info' }
          ],
          [
            { text: '২। ঠিকানাঃ', style: 'info' },
            { text: getValue(companyInfo.address), style: 'info' }
          ],
          [
            { text: '৩। বিনঃ', style: 'info' },
            { text: getValue(companyInfo.vat_no), style: 'info' }
          ],
          [
            { text: '৪। লাইসেন্সের তারিখঃ', style: 'info' },
            { text: getValue(companyInfo.license_date), style: 'info' }
          ],
          [
            { text: '৫। ঘোষিত সহগ অনুযায়ী পণ্য/সেবার প্রথম সরবরাহের তারিখঃ', style: 'info' },
            { text: getRowDate(productCosting.date), style: 'info' }
          ]
        ]
      }
    };
  }

  const getItemTable = (productCosting) => {
    return {
      table: {
        headerRows: 4,
        widths: [30, 60, 90, 50, '*', 52, 52, 44, 44, '*', 52, 44],
        body: getTableBody(productCosting)
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => '#000',
        vLineColor: () => '#000',
        paddingLeft: () => 2,
        paddingRight: () => 2,
        paddingTop: () => 2,
        paddingBottom: () => 2
      }
    };
  }

  const getTableBody = (productCosting) => {
    return [
      ...getTableHead(),
      getNumberRow(),
      getProductRow(productCosting),
      ...getDataRows(productCosting),
      getTotalRow(productCosting)
    ];
  }

  const getTableHead = () => {
    return [
      [
        { text: 'ক্রমিক সংখ্যা', style: 'th', rowSpan: 3 },
        { text: 'পণ্যের এইচ এস কোড / সেবা কোড', style: 'th', rowSpan: 3 },
        { text: 'পণ্যের নাম ও বিবরণ', style: 'th', rowSpan: 3 },
        { text: 'সরবরাহের একক', style: 'th', rowSpan: 3 },
        { text: 'একক পণ্য/সেবা সরবরাহে ব্যবহার্য যাবতীয় উপকরণের/কাঁচামালের এবং প্যাকিং সামগ্রীর বিবরণ, পরিমাণ ও মূল্য (উপকরনভিত্তিক অপচয়ের শতকরা হারসহ)', style: 'th', colSpan: 5 },
        {}, {}, {}, {},
        { text: 'মূল্য সংযোজনের বিবরণ', style: 'th', colSpan: 2 },
        {},
        { text: 'মন্তব্য', style: 'th', rowSpan: 3 }
      ],
      [
        {}, {}, {}, {},
        { text: 'বিবরণ', style: 'th', rowSpan: 2 },
        { text: 'অপচয়সহ পরিমাণ', style: 'th', rowSpan: 2 },
        { text: 'মূল্য', style: 'th', rowSpan: 2 },
        { text: 'অপচয়ের পরিমাণ', style: 'th', colSpan: 2 },
        {},
        { text: 'মূল্য সংযোজনের খাত', style: 'th', rowSpan: 2 },
        { text: 'মূল্য', style: 'th', rowSpan: 2 },
        {}
      ],
      [
        {}, {}, {}, {}, {}, {}, {},
        { text: 'পরিমাণ', style: 'th' },
        { text: 'শতকরা হার', style: 'th' },
        {}, {}, {}
      ]
    ];
  }

  const getNumberRow = () => {
    const nums = ['(১)', '(২)', '(৩)', '(৪)', '(৫)', '(৬)', '(৭)', '(৮)', '(৯)', '(১০)', '(১১)', '(১২)'];
    return nums.map(function (n) {
      return { text: n, style: 'th' };
    });
  }

  const getProductRow = (productCosting) => {
    return [
      { text: '', style: 'td' },
      { text: getValue(productCosting.hs_code), style: 'td' },
      { text: getProductName(productCosting), style: 'tdLeft' },
      { text: getUnitName(productCosting), style: 'tdLeft' },
      { text: '', style: 'td' },
      { text: '', style: 'td' },
      { text: '', style: 'td' },
      { text: '', style: 'td' },
      { text: '', style: 'td' },
      { text: '', style: 'td' },
      { text: '', style: 'td' },
      { text: '', style: 'td' }
    ];
  }

  const getDataRows = (productCosting) => {
    const inputs = getInputItems(productCosting);
    const valueAdditions = getValueAdditionItems(productCosting);
    const rowCount = Math.max(inputs.length, valueAdditions.length);
    if (rowCount < 1) return [getBlankRow()];
    const rows = [];
    let index = 0;
    while (index < rowCount) {
      rows.push(mapRow(inputs[index], valueAdditions[index], index));
      index = index + 1;
    }
    return rows;
  }

  const mapRow = (input, valueAddition, index) => {
    return [
      { text: getSerial(input, index), style: 'td' },
      { text: '', style: 'td' },
      { text: '', style: 'td' },
      { text: '', style: 'td' },
      { text: getItemName(input), style: 'tdLeft' },
      { text: getItemNumber(input, 'total_quantity'), style: 'tdRight' },
      { text: getItemNumber(input, 'amount'), style: 'tdRight' },
      { text: getItemNumber(input, 'wastage_quantity'), style: 'tdRight' },
      { text: getItemNumber(input, 'wastage'), style: 'tdRight' },
      { text: getItemName(valueAddition), style: 'tdLeft' },
      { text: getItemNumber(valueAddition, 'amount'), style: 'tdRight' },
      { text: '', style: 'td' }
    ];
  }

  const getSerial = (input, index) => {
    if (! input) return '';
    return index + 1;
  }

  const getInputItems = (productCosting) => {
    return getGeneral(productCosting).filter(function (item) {
      return ['raw_materials', 'packing_materials'].includes(item.type);
    });
  }

  const getValueAdditionItems = (productCosting) => {
    return getGeneral(productCosting).filter(function (item) {
      return ['direct_labor', 'direct_overhead'].includes(item.type);
    });
  }

  const getGeneral = (productCosting) => {
    if (! productCosting) return [];
    if (! productCosting.general) return [];
    if (! Array.isArray(productCosting.general)) return [];
    return productCosting.general;
  }

  const getItemName = (item) => {
    if (! item) return '';
    if (! item.product_costingable) return '';
    if (item.product_costingable.text) return item.product_costingable.text;
    if (item.product_costingable.name) return item.product_costingable.name;
    return '';
  }

  const getItemNumber = (item, field) => {
    if (! item) return '';
    if (item[field] === undefined) return '';
    if (item[field] === null) return '';
    return formatAmount(item[field]);
  }

  const formatAmount = (value) => {
    const amount = getNumber(value);
    if (amount === 0) return '0.00';
    return commaFormat(amount);
  }

  const getProductionItem = (productCosting) => {
    return getGeneral(productCosting).find(function (item) {
      return item.type === 'production';
    });
  }

  const getProductName = (productCosting) => {
    const production = getProductionItem(productCosting);
    const name = getItemName(production);
    if (name) return name;
    return getValue(productCosting.product_costing_name);
  }

  const getUnitName = (productCosting) => {
    const production = getProductionItem(productCosting);
    if (! production) return '';
    if (! production.unit_of_measurment) return '';
    return production.unit_of_measurment;
  }

  const getBlankRow = () => {
    return Array.from({ length: 12 }, function () {
      return { text: ' ', style: 'td' };
    });
  }

  const getTotalRow = (productCosting) => {
    return [
      { text: '', style: 'td', colSpan: 6 }, {}, {}, {}, {}, {},
      { text: formatAmount(sumAmount(getInputItems(productCosting))), style: 'tdRight' },
      { text: '', style: 'td', colSpan: 3 }, {}, {},
      { text: formatAmount(sumAmount(getValueAdditionItems(productCosting))), style: 'tdRight' },
      { text: '', style: 'td' }
    ];
  }

  const sumAmount = (items) => {
    return items.reduce(function (total, item) {
      return total + getNumber(item.amount);
    }, 0);
  }

  const getNoteBlock = () => {
    return {
      margin: [0, 12, 0, 0],
      stack: [
        { text: 'বিশেষ দ্রষ্টব্যঃ', style: 'footerNote', margin: [0, 0, 0, 3] },
        { text: '১। যেকোনো পণ্য সরবরাহের ন্যূনতম ১৫ দিন পূর্বে অনলাইনে মূসক কম্পিউটার সিস্টেমে বা সংশ্লিষ্ট বিভাগীয় কর্মকর্তার দপ্তরে উপকরণ উৎপাদ সহগ ঘোষণা দাখিল করিতে হইবে।', style: 'footerNote' },
        { text: '২। পণ্যের উপকরণ/কাঁচামাল বা মূল্য সংযোজনের খাতের পরিমাণ বা মূল্যে ৭.৫% এর বেশী পরিবর্তন হইলে নতুন ঘোষণা দাখিল করিতে হইবে।', style: 'footerNote' },
        { text: '৩। পণ্য ক্রয়ের পক্ষে প্রামাণিক দলিল হিসাবে বিল অব এন্ট্রি বা চালানপত্রের কপি সংযুক্ত করিতে হইবে।', style: 'footerNote' }
      ]
    };
  }

  const getFooter = () => {
    return function (currentPage, pageCount) {
      return {
        margin: [25, 10, 25, 0],
        columns: [
          { text: getPrintedAt(), style: 'footerNote' },
          { text: 'Page ' + currentPage + ' of ' + pageCount, alignment: 'right', style: 'footerNote' }
        ]
      };
    };
  }

  const getPrintedAt = () => {
    const now = new Date();
    const datePart = now.toLocaleDateString('en-US');
    const timePart = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: false });
    return datePart + ' ' + timePart;
  }

  const getRowDate = (date) => {
    if (! date) return '';
    return formatDate(new Date(date)).split(' ')[0];
  }

  const getValue = (value) => {
    if (! value) return '';
    return value;
  }

  const getNumber = (value) => {
    const parsed = parseFloat(value);
    if (isNaN(parsed)) return 0;
    return parsed;
  }

  const styles = {
    h1: { fontSize: 9, alignment: 'center' },
    h2: { fontSize: 10, alignment: 'center', bold: true },
    small: { fontSize: 8, alignment: 'center' },
    info: { fontSize: 8, alignment: 'left' },
    th: { fontSize: 7, alignment: 'center', lineHeight: 1.1 },
    td: { fontSize: 7, alignment: 'center' },
    tdRight: { fontSize: 7, alignment: 'right' },
    tdLeft: { fontSize: 7, alignment: 'left' },
    footerNote: { fontSize: 7 }
  };

  return {
    exportToPdfMushak43
  }
}

export default printMushak43;
